/**
 * Pet report cards (migration 0093) — shared wire types + tiny helpers used
 * by the staff composer/panel, the customer booking page, the public
 * live-stay page and the settings editor.
 */

export interface ReportCardPhoto {
  photoId: string;
  fileId: string;
  caption: string | null;
}

export interface ReportCardMedication {
  medicationName: string;
  dose: string | null;
  administeredAt: string;
  notes: string | null;
}

export interface ReportCardView {
  id: string;
  bookingId: string;
  petId: string;
  /** YYYY-MM-DD in the facility's local timezone. */
  reportDate: string;
  moodLabel: string | null;
  notes: string | null;
  photos: ReportCardPhoto[];
  medications: ReportCardMedication[];
  feedingNotes: string | null;
  signOff: string | null;
  sentAt: string | null;
}

export interface ReportCardSettingsView {
  isEnabled: boolean;
  moodLabels: string[];
  signOffLine: string | null;
}

/** Migration 0101 — one logged meal outcome for the report day. */
export interface ReportCardFeedingLogEntry {
  id: string;
  slot: string;
  slotTime: string | null;
  outcome: string;
  givenAt: string;
  notes: string | null;
}

export interface ReportCardComposerContext {
  sendable: boolean;
  bookingType: string;
  settings: ReportCardSettingsView;
  reportDate: string;
  medications: ReportCardMedication[];
  feedingNotes: string | null;
  /** Migration 0101 — today's feeding outcomes (optional for older servers). */
  feedingLog?: ReportCardFeedingLogEntry[];
  feedingPlanText?: string | null;
}

export const REPORT_CARD_BOOKING_TYPES: ReadonlySet<string> = new Set(['boarding', 'daycare']);

export function reportCardThumbUrl(fileId: string): string {
  return `/api/files/${encodeURIComponent(fileId)}/url?variant=thumb`;
}

export function formatReportCardDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function formatReportCardTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}
